import React from "react";
import {
  DatagridConfigurable,
  DateField,
  DateInput,
  FunctionField,
  List,
  NumberField,
  SelectField,
  SelectInput,
  TextField,
  TextInput,
  useResourceContext,
  useTranslate,
} from "react-admin";

import { createBuyerPaymentFilterStatuses, createPaymentStatuses } from "../components/choices";
import { ListActions } from "../components/list_actions";
import { PaymentInvoicesField } from "../components/PaymentInvoicesField";
import { ProductPayment } from "../api/payments";
import { csvExporter } from "../infrastructure/csvExporter";
import { getAdvanceContractIdField } from "../utils/deal";
import {
  getDisplayTradingVolumeValue,
  PriceCalculationMode,
  priceCalculationModeChoices,
} from "../utils/price_calculation";

const getSumBrownRiceInYen = (
  productPayments: ProductPayment[] | undefined,
  priceCalculationMode: PriceCalculationMode
): number => {
  return Array.isArray(productPayments)
    ? productPayments.reduce((sum: number, row) => {
        return (
          sum +
          Number(
            getDisplayTradingVolumeValue({
              price_calculation_mode: priceCalculationMode,
              trading_volume_in_yen: row.trading_volume_in_yen,
              trading_volume_excluding_tax_in_yen: row.trading_volume_excluding_tax_in_yen,
            }) ?? 0
          )
        );
      }, 0)
    : 0;
};

const PaymentOverviewFilter = [
  <SelectInput
    key="status"
    source="status"
    choices={createBuyerPaymentFilterStatuses()}
    // react-adminでデフォルトフィルタの値を未選択にするとクラッシュする問題の回避
    alwaysOn
    parse={(value: any) => {
      if (value === "" || value == null) {
        return undefined;
      }
      return value;
    }}
    format={(value: any) => {
      if (value == null) {
        return "";
      }
      return value;
    }}
  />,
  <TextInput key="producer_company_name" source="producer_company_name" />,
  <DateInput key="handover_date" source="handover_date" label="resources.payments.fields.handover_date_filter" />,
  <DateInput
    key="payment_due_date"
    source="payment_due_date"
    label="resources.payments.fields.payment_due_date_filter"
  />,
  <DateInput key="invoiced_at" source="invoiced_at" label="resources.payments.fields.invoiced_at_filter" />,
];

export const PaymentOverviewList = (props: any) => {
  const resource = useResourceContext();
  const translate = useTranslate();
  const preferenceKey = "payment_overviews";

  return (
    <List
      {...props}
      actions={
        <ListActions filters={PaymentOverviewFilter} preferenceKey={preferenceKey} savedFilterScope="payments" />
      }
      filters={PaymentOverviewFilter}
      sort={{ field: "payment_due_date", order: "ASC" }}
      filterDefaultValues={{ status: "waiting_for_payment_report" }}
      exporter={csvExporter(resource, translate)}
    >
      <DatagridConfigurable
        rowClick={(_id: any, _resource: any, record: any) => `/deals/${record.amend_id}/show/payment`}
        preferenceKey={preferenceKey}
        bulkActionButtons={false}
      >
        <FunctionField source="amend_id" render={(record: any) => getAdvanceContractIdField({ id: record.amend_id })} />
        <TextField source="id" />
        <SelectField source="status" choices={createPaymentStatuses()} sortable={false} />
        <TextField source="producer_company_name" sortable={false} />
        <DateField source="handover_date" />
        <DateField source="payment_due_date" />
        <NumberField source="total_bill_in_yen" sortable={false} />
        <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} sortable={false} />
        <FunctionField
          source="product_payments"
          label="resources.payments.fields.brown_rice_in_yen"
          sortable={false}
          textAlign="right"
          render={(record: any) =>
            getSumBrownRiceInYen(record.product_payments, record.price_calculation_mode).toLocaleString()
          }
        />
        <NumberField source="product_payment_adjustments_in_yen" sortable={false} />
        <NumberField source="commission_fee_adjustments_in_yen" sortable={false} />
        <NumberField
          source="commission_fee_in_yen"
          label="resources.payments.fields.business_consignment_fee_in_yen"
          sortable={false}
        />
        <NumberField source="delivery_fee_in_yen" sortable={false} />
        <NumberField source="delivery_agency_fee_in_yen" sortable={false} />
        <DateField source="invoiced_at" />
        <PaymentInvoicesField label="resources.payments.fields.invoices" />
      </DatagridConfigurable>
    </List>
  );
};
